// Phase 15.1 r2 — shared financial info-pane block for CaseView + CollectiveCaseView.
//
// Four sections rendered as siblings with independent gates so the two
// views stay in lockstep (the original CV/CC asymmetry — nested vs sibling
// IIFEs — meant each view rendered a different subset of the four).
//
//   1. Claim       — unconditional; renders when cs.claimAmount != null
//   2. Terms       — gated by canTerms; renders when ref-rate or costs set
//   3. Success fee — gated by canFin;   renders when claim or fee set
//   4. Financials  — gated by canFin;   renders when there are time entries
//                                       or payments to display
//
// The split mirrors shapes.js: TERMS_FIELDS (referenceHourlyRate, costsRatio)
// gate by caseView.terms; FINANCIAL_FIELDS (successFeeRate, payments) gate
// by caseView.financials. claimAmount left FINANCIAL_FIELDS in Phase 15
// (D-A) so the claim row is ungated. The same split lives in the case edit
// modal (App.jsx case"ca" + CollectiveCaseView CC modal, Phase 15 Stage 3 r2).
//
// C.2 — the computed money (exVAT/vatIncl, Total/Billed/Unbilled, Balance
// due, Bruto/Netto success fee) moved server-side: the panel posts the rows
// it holds (POST /api/financials/compute, reduced via financialsClient) and
// renders what it is handed. Latest-wins; the result is stamped with the
// owning matter id+kind so a case switch never shows another case's money;
// fq(null) renders "—" until the first response lands. The rr/cr field
// echoes (Reference rate, Costs %) are inputs, not computed money — they
// stay client-side.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "../i18n";
import { fq } from "../utils";
// 191B §A (item 8) — the rate in force: the stored rate (0 included), else the
// one default the money counts with. The form and the pro forma prefill read
// the same function; `?? 15` stood here before.
import { successFeeRateOf } from "../lib/successFee.mjs";
import type { CSSProperties } from "react";
import { FS } from "../lib/tokens";
import { computeCaseFinancials } from "../api";
import { redMatterFin, redEntryFin, redCoFin, redRoleRows,
  redFinInv,
} from "../lib/financialsClient";
import { useRoleLabels } from "../hooks/useRoleLabels";

const ROW_STYLE: CSSProperties = {
  flexDirection: "column",
  gap: 2,
  alignItems: "flex-start",
  paddingTop: 5,
  paddingBottom: 5,
};
const CONTENT_STYLE: CSSProperties = {
  fontSize: FS.s9_5,
  color: "var(--ts)",
  fontFamily: "'JetBrains Mono',monospace",
  lineHeight: 2,
  marginTop: 2,
};
const FIN_CONTENT_STYLE: CSSProperties = {
  fontSize: FS.s9_5,
  fontFamily: "'JetBrains Mono',monospace",
  lineHeight: 1.9,
  marginTop: 2,
  color: "var(--ts)",
};

type CaseFinancialPanelProps = {
  kind?: string;
  cs?: any;
  te?: any[];
  co?: any[];
  // §3b — the invoices whose stored vatRate prices their own entries. Two
  // fields, because two is all redFinInv reads.
  inv?: readonly { id?: number | null; vatRate?: number | null }[];
  canTerms?: boolean;
  canFin?: boolean;
};

export default function CaseFinancialPanel({
  kind,
  cs,
  inv,
  te,
  co,
  canTerms,
  canFin,
}: CaseFinancialPanelProps) {
  const { t } = useTranslation();
  const { rows: _roleRows } = useRoleLabels();
  const [calc, setCalc] = useState<any>(null);
  const _seq = useRef(0);
  useEffect(() => {
    if (!cs) {
      setCalc(null);
      return;
    }
    const seq = ++_seq.current;
    computeCaseFinancials({
      ...(kind === "cc"
        ? { collectiveCases: [redMatterFin(cs)] }
        : { cases: [redMatterFin(cs)] }),
      entries: (te || [])
        .filter((e) => (kind === "cc" ? e.ccId === cs.id : e.caId === cs.id))
        .map(redEntryFin),
      collaborators: (co || []).map(redCoFin),
      roleRows: redRoleRows(_roleRows),
      // BRIEF 87 §3b — the panel sums this matter's entries, so it needs the
      // per-entry VAT the other three call sites already send. Without it every
      // lookup misses and a frozen entry silently reverts to the FIRM's current
      // rate here while the table beside it shows the invoiced one — two figures
      // for the same money on one screen. Carried as an owed item for exactly
      // one commit; this closes it rather than letting §6/§7 touch the file and
      // not notice.
      invoices: (inv || []).map(redFinInv),
    })
      .then((r: any) => {
        if (_seq.current !== seq) return;
        const row = (kind === "cc" ? r?.perCc : r?.perCase)?.[0] ?? null;
        setCalc({ id: cs.id, kind, data: row ? row.panel : null });
      })
      .catch(() => {
        if (_seq.current === seq) setCalc(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cs, te, co, kind, _roleRows]);
  if (!cs) return null;

  const fin =
    calc && calc.id === cs.id && calc.kind === kind ? calc.data : null;

  // Field echoes (inputs, not computed money) — client-side as before.
  const cr = cs.costsRatio ?? 19;
  const rr = cs.referenceHourlyRate ?? 0;
  // Server-handed money (null → fq renders "—" until the compute lands).
  const exVAT = fin ? fin.exVAT : null;
  const vatIncl = fin ? fin.vatIncl : null;
  const bruto = fin ? fin.bruto : null;
  const netto = fin ? fin.netto : null;
  const totV = fin ? fin.totV : null;
  const bilV = fin ? fin.bilV : null;
  const unbV = fin ? fin.unbV : null;
  const paidV = fin ? fin.paidV : null;
  const balV = fin ? fin.balV : null;

  // Phase 15.1 r3 — Claim is the only section that still gates on data:
  // "Claim: — EUR" on cases without a claim is noise. Terms / Success fee /
  // Financials are rendered unconditionally for permission-bearing users;
  // empty fields fall back to the ?? defaults (cr=19%; the success-fee rate
  // through successFeeRateOf — 191B §A, one definition with the money)
  // and the computed figures show 0.00 EUR.
  const hasClaim = cs.claimAmount != null;

  return (
    <>
      {hasClaim && (
        <div className="detail-row">
          <span className="detail-label">
            {t("case.claim.label", { fallback: "Claim" })}
          </span>
          <span className="detail-value mono">{fq(cs.claimAmount)} €</span>
        </div>
      )}
      {canTerms && (
        <div className="detail-row" style={ROW_STYLE}>
          <span className="detail-label">
            {t("case.field.terms", { fallback: "Terms" })}
          </span>
          <div style={CONTENT_STYLE}>
            {t("case.terms.reference_rate", { fallback: "Reference rate" })}:{" "}
            {fq(rr)} €
            {" | "}
            {t("case.terms.costs", { fallback: "Costs" })}: {cr}%{" | "}
            {t("case.terms.exvat", { fallback: "ExVAT" })}: {fq(exVAT)} €
            {" | "}
            {t("case.terms.vat_incl", { fallback: "VAT Incl." })}: {fq(vatIncl)}{" "}
            €
          </div>
        </div>
      )}
      {canFin && (
        <div className="detail-row" style={ROW_STYLE}>
          <span className="detail-label">
            {t("case.field.financials", { fallback: "Financials" })}
          </span>
          <div style={FIN_CONTENT_STYLE}>
            {t("case.fin.total", { fallback: "Total" })}: {fq(totV)} €
            {" | "}
            {t("case.fin.billed", { fallback: "Billed" })}: {fq(bilV)} €
            {" | "}
            <span style={{ color: "var(--am)" }}>
              {t("case.fin.unbilled", { fallback: "Unbilled" })}: {fq(unbV)} €
            </span>
            {" | "}
            {t("case.fin.paid", { fallback: "Paid" })}: {fq(paidV)} €
            {" | "}
            <span style={{ color: (balV ?? 0) > 0 ? "var(--rd)" : "var(--gn)" }}>
              {t("case.fin.balance_due", { fallback: "Balance due" })}:{" "}
              {fq(balV)} €
            </span>
          </div>
        </div>
      )}
      {canFin && (
        <div className="detail-row" style={ROW_STYLE}>
          <span className="detail-label">
            {t("case.claim.success_fee", { fallback: "Success fee" })}
          </span>
          <div style={CONTENT_STYLE}>
            {t("case.claim.success_fee", { fallback: "Success fee" })}:{" "}
            {successFeeRateOf(cs)}%{" | "}
            {t("case.claim.gross", { fallback: "Bruto" })}: {fq(bruto)} €
            {" | "}
            {t("case.claim.net", { fallback: "Netto" })}:{" "}
            <span style={{ color: (netto ?? 0) < 0 ? "var(--rd)" : undefined }}>
              {fq(netto)} €
            </span>
          </div>
        </div>
      )}
    </>
  );
}
